import { z } from "zod";
import type { AgentControlRunResult, ScoutEvidence } from "./agent-control";
import type { AsymmetricState } from "./asymmetric-read-model";
import type { MediaStyleProvenance, MediaStyleSystem } from "./media-style";
import type { MediaModelDefaults } from "./media-model-routing";

export const mediaArticleTemplate = {
  id: "engine-article",
  version: 1,
  name: "Engine article",
  sections: [
    { id: "signal", heading: "Signal" },
    { id: "mechanism", heading: "Mechanism" },
    { id: "trajectory", heading: "Trajectory" },
    { id: "decision", heading: "Decision lens" },
    { id: "falsification", heading: "What would change the view" },
  ],
} as const;

export const mediaOutputFormats = [
  { id: "article", label: "Article", description: "Long-form engine analysis" },
  { id: "x-post", label: "X / Twitter", description: "Single post or concise thread" },
  { id: "threads-post", label: "Threads", description: "Meta Threads post or sequence" },
  { id: "instagram-post", label: "Instagram", description: "Finished carousel images + caption" },
  { id: "instagram-reel", label: "Instagram Reel", description: "Vertical edited-video package" },
  { id: "youtube-video", label: "YouTube", description: "Long-form video package" },
  { id: "youtube-short", label: "YouTube Short", description: "Vertical short-form package" },
  { id: "youtube-live", label: "YouTube Live", description: "Interactive live run-of-show" },
  { id: "tiktok", label: "TikTok", description: "Native vertical video package" },
  { id: "twitch-live", label: "Twitch Live", description: "Chat-led live run-of-show" },
] as const;

export type MediaOutputType = (typeof mediaOutputFormats)[number]["id"];
export type MediaPlatformType = Exclude<MediaOutputType, "article">;

export function mediaVariants(type: MediaOutputType): readonly string[] {
  return type === "article" ? ["analysis", "brief"] : type === "instagram-post" ? ["single", "carousel"] : [];
}

export const mediaPlatformTemplates = {
  "x-post": { id: "x-post", version: 1, name: "X / Twitter post" },
  "threads-post": { id: "threads-post", version: 1, name: "Threads post" },
  "instagram-post": { id: "instagram-post", version: 1, name: "Instagram post" },
  "instagram-reel": { id: "instagram-reel", version: 1, name: "Instagram Reel" },
  "youtube-video": { id: "youtube-video", version: 1, name: "YouTube video" },
  "youtube-short": { id: "youtube-short", version: 1, name: "YouTube Short" },
  "youtube-live": { id: "youtube-live", version: 1, name: "YouTube Live" },
  tiktok: { id: "tiktok", version: 1, name: "TikTok video" },
  "twitch-live": { id: "twitch-live", version: 1, name: "Twitch live stream" },
} as const satisfies Record<MediaPlatformType, { id: string; version: 1; name: string }>;

export type MediaArticleSectionId = (typeof mediaArticleTemplate.sections)[number]["id"];

const paragraphSchema = z.string().trim().min(40).max(2_400);

export const generatedMediaArticleSchema = z.object({
  title: z.string().trim().min(8).max(140),
  dek: z.string().trim().min(20).max(320),
  thesis: z.string().trim().min(20).max(700),
  sections: z.object({
    signal: z.array(paragraphSchema).min(1).max(4),
    mechanism: z.array(paragraphSchema).min(1).max(4),
    trajectory: z.array(paragraphSchema).min(1).max(4),
    decision: z.array(paragraphSchema).min(1).max(4),
    falsification: z.array(paragraphSchema).min(1).max(4),
  }),
  limitations: z.array(z.string().trim().min(8).max(500)).max(8),
  confidence: z.number().int().min(0).max(100),
});

export type GeneratedMediaArticle = z.infer<typeof generatedMediaArticleSchema>;

const evidenceNumbersSchema = z.array(z.number().int().min(1).max(10_000)).min(1).max(20);
const platformLimitationsSchema = z.array(z.string().trim().min(8).max(500)).max(8);
const platformConfidenceSchema = z.number().int().min(0).max(100);
// Keep the structured-output schema to the JSON Schema subset accepted by the
// model API. Unicode property escapes are valid JavaScript but are rejected by
// the API's schema validator, so the exact character check happens after the
// typed output is returned.
const hashtagSchema = z.string().trim().min(2).max(60);
const hashtagPattern = /^[\p{L}\p{N}_]+$/u;

export const generatedXPostSchema = z.object({
  title: z.string().trim().min(8).max(140),
  format: z.enum(["single", "thread"]),
  posts: z.array(z.object({
    text: z.string().trim().min(20).max(280),
    evidenceNumbers: evidenceNumbersSchema,
  })).min(1).max(6),
  visualDirection: z.string().trim().min(12).max(500),
  limitations: platformLimitationsSchema,
  confidence: platformConfidenceSchema,
});

export const generatedThreadsPostSchema = z.object({
  title: z.string().trim().min(8).max(140),
  format: z.enum(["single", "thread"]),
  posts: z.array(z.object({
    text: z.string().trim().min(20).max(500),
    evidenceNumbers: evidenceNumbersSchema,
  })).min(1).max(8),
  visualDirection: z.string().trim().min(12).max(500),
  limitations: platformLimitationsSchema,
  confidence: platformConfidenceSchema,
});

export const generatedInstagramPostSchema = z.object({
  title: z.string().trim().min(8).max(140),
  format: z.enum(["single-image", "carousel"]),
  visualConcept: z.string().trim().min(20).max(700),
  slides: z.array(z.object({
    headline: z.string().trim().min(3).max(120),
    body: z.string().trim().min(8).max(500),
    evidenceNumbers: evidenceNumbersSchema,
  })).min(1).max(10),
  caption: z.string().trim().min(40).max(2_000),
  captionEvidenceNumbers: evidenceNumbersSchema,
  altText: z.string().trim().min(20).max(1_000),
  hashtags: z.array(hashtagSchema).min(3).max(8),
  limitations: platformLimitationsSchema,
  confidence: platformConfidenceSchema,
});

export const generatedYoutubeVideoSchema = z.object({
  title: z.string().trim().min(8).max(100),
  thumbnailText: z.string().trim().min(3).max(48),
  description: z.string().trim().min(40).max(4_500),
  descriptionEvidenceNumbers: evidenceNumbersSchema,
  durationSeconds: z.number().int().min(360).max(900),
  hook: z.string().trim().min(30).max(1_000),
  hookEvidenceNumbers: evidenceNumbersSchema,
  sections: z.array(z.object({
    heading: z.string().trim().min(3).max(100),
    narration: z.string().trim().min(80).max(3_500),
    visualDirection: z.string().trim().min(12).max(700),
    evidenceNumbers: evidenceNumbersSchema,
  })).min(3).max(8),
  close: z.string().trim().min(20).max(700),
  closeEvidenceNumbers: evidenceNumbersSchema,
  limitations: platformLimitationsSchema,
  confidence: platformConfidenceSchema,
});

const shortVideoBeatSchema = z.object({
  role: z.enum(["hook", "body", "close"]),
  startSecond: z.number().int().min(0).max(180),
  endSecond: z.number().int().min(1).max(180),
  spokenLine: z.string().trim().min(4).max(600),
  onScreenText: z.string().trim().min(1).max(160),
  visualDirection: z.string().trim().min(8).max(500),
  evidenceNumbers: evidenceNumbersSchema,
});

export const generatedInstagramReelSchema = z.object({
  title: z.string().trim().min(8).max(140),
  coverText: z.string().trim().min(3).max(48),
  durationSeconds: z.number().int().min(15).max(180),
  beats: z.array(shortVideoBeatSchema).min(3).max(12),
  audioDirection: z.string().trim().min(8).max(500),
  caption: z.string().trim().min(20).max(2_000),
  captionEvidenceNumbers: evidenceNumbersSchema,
  hashtags: z.array(hashtagSchema).min(3).max(8),
  limitations: platformLimitationsSchema,
  confidence: platformConfidenceSchema,
});

export const generatedYoutubeShortSchema = z.object({
  title: z.string().trim().min(8).max(100),
  durationSeconds: z.number().int().min(15).max(180),
  beats: z.array(shortVideoBeatSchema).min(3).max(12),
  caption: z.string().trim().min(20).max(1_000),
  captionEvidenceNumbers: evidenceNumbersSchema,
  limitations: platformLimitationsSchema,
  confidence: platformConfidenceSchema,
});

export const generatedTiktokSchema = z.object({
  title: z.string().trim().min(8).max(140),
  durationSeconds: z.number().int().min(12).max(90),
  beats: z.array(shortVideoBeatSchema).min(3).max(10),
  soundDirection: z.string().trim().min(8).max(500),
  caption: z.string().trim().min(20).max(1_500),
  captionEvidenceNumbers: evidenceNumbersSchema,
  hashtags: z.array(hashtagSchema).min(3).max(8),
  limitations: platformLimitationsSchema,
  confidence: platformConfidenceSchema,
});

const liveSegmentSchema = z.object({
  role: z.enum(["opening", "analysis", "interaction", "break", "close"]),
  startMinute: z.number().int().min(0).max(240),
  endMinute: z.number().int().min(1).max(240),
  heading: z.string().trim().min(3).max(120),
  hostNotes: z.string().trim().min(40).max(2_000),
  visualDirection: z.string().trim().min(8).max(500),
  audiencePrompt: z.string().trim().max(300),
  evidenceNumbers: evidenceNumbersSchema,
});

export const generatedYoutubeLiveSchema = z.object({
  title: z.string().trim().min(8).max(100),
  thumbnailText: z.string().trim().min(3).max(48),
  description: z.string().trim().min(40).max(4_500),
  descriptionEvidenceNumbers: evidenceNumbersSchema,
  durationMinutes: z.number().int().min(30).max(120),
  streamFormat: z.enum(["horizontal", "vertical", "dual"]),
  segments: z.array(liveSegmentSchema).min(4).max(12),
  pinnedChatPrompt: z.string().trim().min(8).max(200),
  moderatorBrief: z.string().trim().min(20).max(1_000),
  preShowChecklist: z.array(z.string().trim().min(4).max(240)).min(3).max(10),
  replayPlan: z.string().trim().min(20).max(700),
  limitations: platformLimitationsSchema,
  confidence: platformConfidenceSchema,
});

export const generatedTwitchLiveSchema = z.object({
  title: z.string().trim().min(8).max(120),
  description: z.string().trim().min(40).max(2_000),
  descriptionEvidenceNumbers: evidenceNumbersSchema,
  category: z.string().trim().min(2).max(80),
  tags: z.array(hashtagSchema).min(3).max(10),
  durationMinutes: z.number().int().min(45).max(180),
  segments: z.array(liveSegmentSchema).min(4).max(14),
  chatPrompt: z.string().trim().min(8).max(300),
  moderatorBrief: z.string().trim().min(20).max(1_000),
  preShowChecklist: z.array(z.string().trim().min(4).max(240)).min(3).max(10),
  sceneDirection: z.string().trim().min(20).max(700),
  clipPlan: z.string().trim().min(20).max(700),
  limitations: platformLimitationsSchema,
  confidence: platformConfidenceSchema,
});

export type GeneratedXPost = z.infer<typeof generatedXPostSchema>;
export type GeneratedThreadsPost = z.infer<typeof generatedThreadsPostSchema>;
export type GeneratedInstagramPost = z.infer<typeof generatedInstagramPostSchema>;
export type GeneratedInstagramReel = z.infer<typeof generatedInstagramReelSchema>;
export type GeneratedYoutubeVideo = z.infer<typeof generatedYoutubeVideoSchema>;
export type GeneratedYoutubeShort = z.infer<typeof generatedYoutubeShortSchema>;
export type GeneratedYoutubeLive = z.infer<typeof generatedYoutubeLiveSchema>;
export type GeneratedTiktok = z.infer<typeof generatedTiktokSchema>;
export type GeneratedTwitchLive = z.infer<typeof generatedTwitchLiveSchema>;

export type GeneratedPlatformMedia =
  | { type: "x-post"; content: GeneratedXPost }
  | { type: "threads-post"; content: GeneratedThreadsPost }
  | { type: "instagram-post"; content: GeneratedInstagramPost }
  | { type: "instagram-reel"; content: GeneratedInstagramReel }
  | { type: "youtube-video"; content: GeneratedYoutubeVideo }
  | { type: "youtube-short"; content: GeneratedYoutubeShort }
  | { type: "youtube-live"; content: GeneratedYoutubeLive }
  | { type: "tiktok"; content: GeneratedTiktok }
  | { type: "twitch-live"; content: GeneratedTwitchLive };

export type MediaCitation = {
  number: number;
  recordId: string;
  kind: ScoutEvidence["kind"];
  title: string;
  observation: string;
  relevance: string;
  support: ScoutEvidence["support"];
  url: string | null;
};

export type MediaGeneratedAsset = {
  id: string;
  role: "instagram-post-image" | "instagram-post-slide" | "instagram-reel-cover";
  mimeType: "image/jpeg";
  width: number;
  height: number;
  model: string;
  altText: string;
  generatedAt: string;
  frameIndex?: number;
  frameCount?: number;
};

export type MediaAssetPayload = {
  metadata: MediaGeneratedAsset;
  bytes: Uint8Array;
};

export type MediaArticleDraft = {
  id: string;
  type: "article";
  status: "draft";
  publicationStatus: "not_published";
  trigger: "manual";
  templateId: typeof mediaArticleTemplate.id;
  templateVersion: typeof mediaArticleTemplate.version;
  objective: string;
  title: string;
  dek: string;
  thesis: string;
  sections: Record<MediaArticleSectionId, string[]>;
  limitations: string[];
  confidence: number;
  citations: MediaCitation[];
  agentRunId: string;
  evaluatorScore: number;
  consultedDomains: string[];
  wordCount: number;
  generatedAt: string;
  style?: MediaStyleProvenance;
};

type MediaPlatformDraftBase = {
  id: string;
  status: "draft";
  publicationStatus: "not_published";
  trigger: "manual";
  templateId: string;
  templateVersion: 1;
  objective: string;
  title: string;
  limitations: string[];
  confidence: number;
  citations: MediaCitation[];
  agentRunId: string;
  evaluatorScore: number;
  consultedDomains: string[];
  generatedAt: string;
  style?: MediaStyleProvenance;
};

export type MediaXPostDraft = MediaPlatformDraftBase & { type: "x-post"; content: GeneratedXPost };
export type MediaThreadsPostDraft = MediaPlatformDraftBase & { type: "threads-post"; content: GeneratedThreadsPost };
export type MediaInstagramPostDraft = MediaPlatformDraftBase & {
  type: "instagram-post";
  content: GeneratedInstagramPost;
  assets?: MediaGeneratedAsset[];
};
export type MediaInstagramReelDraft = MediaPlatformDraftBase & {
  type: "instagram-reel";
  content: GeneratedInstagramReel;
  assets?: MediaGeneratedAsset[];
};
export type MediaYoutubeVideoDraft = MediaPlatformDraftBase & { type: "youtube-video"; content: GeneratedYoutubeVideo };
export type MediaYoutubeShortDraft = MediaPlatformDraftBase & { type: "youtube-short"; content: GeneratedYoutubeShort };
export type MediaYoutubeLiveDraft = MediaPlatformDraftBase & { type: "youtube-live"; content: GeneratedYoutubeLive };
export type MediaTiktokDraft = MediaPlatformDraftBase & { type: "tiktok"; content: GeneratedTiktok };
export type MediaTwitchLiveDraft = MediaPlatformDraftBase & { type: "twitch-live"; content: GeneratedTwitchLive };

export type MediaPlatformDraft =
  | MediaXPostDraft
  | MediaThreadsPostDraft
  | MediaInstagramPostDraft
  | MediaInstagramReelDraft
  | MediaYoutubeVideoDraft
  | MediaYoutubeShortDraft
  | MediaYoutubeLiveDraft
  | MediaTiktokDraft
  | MediaTwitchLiveDraft;

export type MediaDraft = MediaArticleDraft | MediaPlatformDraft;

export type MediaState = {
  configured: boolean;
  modelDefaults?: MediaModelDefaults;
  template: typeof mediaArticleTemplate;
  formats: typeof mediaOutputFormats;
  styleSystem: MediaStyleSystem;
  drafts: MediaDraft[];
};

function candidateIds(value: string) {
  const pieces = value.split(":");
  return Array.from(new Set([
    value,
    pieces.slice(1).join(":"),
    pieces.at(-1) ?? value,
  ].filter(Boolean)));
}

function evidenceUrl(evidence: ScoutEvidence, state: AsymmetricState) {
  const ids = candidateIds(evidence.id);
  const source = state.connectors.find((connector) => ids.includes(connector.id));
  if (source) return source.docsUrl;

  for (const packet of state.signalPackets) {
    if (ids.includes(packet.id) || ids.includes(packet.episode.videoId)) return packet.episode.url;
    const claim = packet.claims.find((candidate) => ids.includes(candidate.id));
    if (claim) return claim.sourceUrl;
    const market = packet.markets.find((candidate) =>
      evidence.id === `market:${candidate.platform}:${candidate.marketId}`
      || ids.includes(candidate.marketId));
    if (market) return market.url;
  }
  return null;
}

export function buildMediaEvidence(
  evidence: ScoutEvidence[],
  state: AsymmetricState,
): MediaCitation[] {
  const unique = Array.from(new Map(evidence.map((record) => [
    `${record.kind}:${record.id}`,
    record,
  ])).values());
  return unique.map((record, index) => ({
    number: index + 1,
    recordId: record.id,
    kind: record.kind,
    title: record.title,
    observation: record.observation,
    relevance: record.relevance,
    support: record.support,
    url: evidenceUrl(record, state),
  }));
}

function sectionText(article: GeneratedMediaArticle, section: MediaArticleSectionId) {
  return article.sections[section].join("\n");
}

function citedNumbers(article: GeneratedMediaArticle) {
  const text = [
    article.thesis,
    ...mediaArticleTemplate.sections.flatMap((section) => article.sections[section.id]),
    ...article.limitations,
  ].join("\n");
  return Array.from(text.matchAll(/\[(\d+)\]/g), (match) => Number(match[1]));
}

export function validateMediaArticleGrounding(
  article: GeneratedMediaArticle,
  evidence: MediaCitation[],
) {
  const citations = Array.from(new Set(citedNumbers(article))).sort((left, right) => left - right);
  const allowed = new Set(evidence.map((record) => record.number));
  const invalid = citations.filter((number) => !allowed.has(number));
  if (!citations.length || invalid.length) throw new Error("MEDIA_ARTICLE_GROUNDING_FAILED");

  for (const section of mediaArticleTemplate.sections) {
    if (!/\[\d+\]/.test(sectionText(article, section.id))) {
      throw new Error(`MEDIA_ARTICLE_SECTION_UNCITED:${section.id}`);
    }
  }

  return citations;
}

function countWords(article: GeneratedMediaArticle) {
  return [
    article.title,
    article.dek,
    article.thesis,
    ...mediaArticleTemplate.sections.flatMap((section) => article.sections[section.id]),
  ].join(" ").replace(/\[\d+\]/g, " ").trim().split(/\s+/).filter(Boolean).length;
}

export function buildMediaArticleDraft(input: {
  id: string;
  objective: string;
  agentRunId: string;
  generatedAt: string;
  article: GeneratedMediaArticle;
  evidence: MediaCitation[];
  coreResult: AgentControlRunResult;
  style?: MediaStyleProvenance;
}): MediaArticleDraft {
  const cited = validateMediaArticleGrounding(input.article, input.evidence);
  const citedSet = new Set(cited);
  return {
    id: input.id,
    type: "article",
    status: "draft",
    publicationStatus: "not_published",
    trigger: "manual",
    templateId: mediaArticleTemplate.id,
    templateVersion: mediaArticleTemplate.version,
    objective: input.objective,
    title: input.article.title,
    dek: input.article.dek,
    thesis: input.article.thesis,
    sections: input.article.sections,
    limitations: input.article.limitations,
    confidence: Math.min(
      input.article.confidence,
      input.coreResult.synthesizer.confidence,
      input.coreResult.evaluator.score,
    ),
    citations: input.evidence.filter((record) => citedSet.has(record.number)),
    agentRunId: input.agentRunId,
    evaluatorScore: input.coreResult.evaluator.score,
    consultedDomains: (input.coreResult.domains ?? [])
      .filter((domain) => domain.status !== "not_consulted")
      .map((domain) => domain.id),
    wordCount: countWords(input.article),
    generatedAt: input.generatedAt,
    style: input.style,
  };
}

function platformEvidenceNumbers(generated: GeneratedPlatformMedia) {
  switch (generated.type) {
    case "x-post":
    case "threads-post":
      return generated.content.posts.flatMap((post) => post.evidenceNumbers);
    case "instagram-post":
      return [
        ...generated.content.captionEvidenceNumbers,
        ...generated.content.slides.flatMap((slide) => slide.evidenceNumbers),
      ];
    case "youtube-video":
      return [
        ...generated.content.descriptionEvidenceNumbers,
        ...generated.content.hookEvidenceNumbers,
        ...generated.content.sections.flatMap((section) => section.evidenceNumbers),
        ...generated.content.closeEvidenceNumbers,
      ];
    case "instagram-reel":
    case "youtube-short":
    case "tiktok":
      return [
        ...generated.content.captionEvidenceNumbers,
        ...generated.content.beats.flatMap((beat) => beat.evidenceNumbers),
      ];
    case "youtube-live":
    case "twitch-live":
      return [
        ...generated.content.descriptionEvidenceNumbers,
        ...generated.content.segments.flatMap((segment) => segment.evidenceNumbers),
      ];
  }
}

function platformPublicText(generated: GeneratedPlatformMedia) {
  switch (generated.type) {
    case "x-post":
    case "threads-post":
      return generated.content.posts.map((post) => post.text);
    case "instagram-post":
      return [generated.content.caption, ...generated.content.slides.flatMap((slide) => [slide.headline, slide.body])];
    case "youtube-video":
      return [
        generated.content.title,
        generated.content.description,
        generated.content.hook,
        ...generated.content.sections.map((section) => section.narration),
        generated.content.close,
      ];
    case "instagram-reel":
    case "youtube-short":
    case "tiktok":
      return [generated.content.caption, ...generated.content.beats.map((beat) => beat.spokenLine)];
    case "youtube-live":
    case "twitch-live":
      return [
        generated.content.title,
        generated.content.description,
        ...generated.content.segments.flatMap((segment) => [segment.heading, segment.hostNotes, segment.audiencePrompt]),
      ];
  }
}

function normalizePlatformTags(tags: string[]) {
  return Array.from(new Set(tags
    .map((tag) => tag
      .trim()
      .replace(/^#+/u, "")
      .replace(/[^\p{L}\p{N}_]+/gu, ""))
    .filter(Boolean)));
}

function normalizeMediaPlatformGenerated(generated: GeneratedPlatformMedia): GeneratedPlatformMedia {
  switch (generated.type) {
    case "instagram-post":
    case "instagram-reel":
    case "tiktok":
      return {
        ...generated,
        content: { ...generated.content, hashtags: normalizePlatformTags(generated.content.hashtags) },
      } as GeneratedPlatformMedia;
    case "twitch-live":
      return {
        ...generated,
        content: { ...generated.content, tags: normalizePlatformTags(generated.content.tags) },
      };
    default:
      return generated;
  }
}

function validateBeatTiming(
  beats: GeneratedInstagramReel["beats"] | GeneratedYoutubeShort["beats"] | GeneratedTiktok["beats"],
  durationSeconds: number,
) {
  let cursor = 0;
  for (const beat of beats) {
    if (beat.startSecond < cursor || beat.endSecond <= beat.startSecond || beat.endSecond > durationSeconds) {
      throw new Error("MEDIA_PLATFORM_TIMING_FAILED");
    }
    cursor = beat.endSecond;
  }
  if (beats[0]?.role !== "hook" || beats.at(-1)?.role !== "close") {
    throw new Error("MEDIA_PLATFORM_STRUCTURE_FAILED");
  }
}

function validateLiveTiming(
  segments: GeneratedYoutubeLive["segments"] | GeneratedTwitchLive["segments"],
  durationMinutes: number,
) {
  let cursor = 0;
  for (const segment of segments) {
    if (segment.startMinute < cursor || segment.endMinute <= segment.startMinute || segment.endMinute > durationMinutes) {
      throw new Error("MEDIA_PLATFORM_TIMING_FAILED");
    }
    cursor = segment.endMinute;
  }
  if (segments[0]?.role !== "opening" || segments.at(-1)?.role !== "close") {
    throw new Error("MEDIA_PLATFORM_STRUCTURE_FAILED");
  }
  if (!segments.some((segment) => segment.role === "interaction")) {
    throw new Error("MEDIA_PLATFORM_STRUCTURE_FAILED");
  }
}

export function validateMediaPlatformGrounding(
  generated: GeneratedPlatformMedia,
  evidence: MediaCitation[],
) {
  const citations = Array.from(new Set(platformEvidenceNumbers(generated))).sort((left, right) => left - right);
  const allowed = new Set(evidence.map((record) => record.number));
  if (!citations.length || citations.some((number) => !allowed.has(number))) {
    throw new Error("MEDIA_PLATFORM_GROUNDING_FAILED");
  }
  if (platformPublicText(generated).some((text) => /\[\d+\]/.test(text))) {
    throw new Error("MEDIA_PLATFORM_PUBLIC_CITATION_MARKER");
  }

  const hashtags = generated.type === "instagram-post"
    || generated.type === "instagram-reel"
    || generated.type === "tiktok"
    ? generated.content.hashtags
    : generated.type === "twitch-live"
      ? generated.content.tags
      : [];
  if (hashtags.some((tag) => !hashtagPattern.test(tag))) {
    throw new Error("MEDIA_PLATFORM_HASHTAG_FAILED");
  }

  if (generated.type === "x-post") {
    if (generated.content.format === "single" && generated.content.posts.length !== 1) {
      throw new Error("MEDIA_PLATFORM_STRUCTURE_FAILED");
    }
    if (generated.content.format === "thread" && generated.content.posts.length < 2) {
      throw new Error("MEDIA_PLATFORM_STRUCTURE_FAILED");
    }
    if (generated.content.posts.some((post) => Array.from(post.text).length > 280)) {
      throw new Error("MEDIA_PLATFORM_LIMIT_FAILED");
    }
  }
  if (generated.type === "threads-post") {
    if (generated.content.format === "single" && generated.content.posts.length !== 1) {
      throw new Error("MEDIA_PLATFORM_STRUCTURE_FAILED");
    }
    if (generated.content.format === "thread" && generated.content.posts.length < 2) {
      throw new Error("MEDIA_PLATFORM_STRUCTURE_FAILED");
    }
    if (generated.content.posts.some((post) => Array.from(post.text).length > 500)) {
      throw new Error("MEDIA_PLATFORM_LIMIT_FAILED");
    }
  }
  if (generated.type === "instagram-post") {
    if (generated.content.format === "single-image" && generated.content.slides.length !== 1) {
      throw new Error("MEDIA_PLATFORM_STRUCTURE_FAILED");
    }
    if (generated.content.format === "carousel" && generated.content.slides.length < 2) {
      throw new Error("MEDIA_PLATFORM_STRUCTURE_FAILED");
    }
  }
  if (generated.type === "instagram-reel" || generated.type === "youtube-short" || generated.type === "tiktok") {
    validateBeatTiming(generated.content.beats, generated.content.durationSeconds);
  }
  if (generated.type === "youtube-live" || generated.type === "twitch-live") {
    validateLiveTiming(generated.content.segments, generated.content.durationMinutes);
  }
  return citations;
}

export function buildMediaPlatformDraft(input: {
  id: string;
  objective: string;
  agentRunId: string;
  generatedAt: string;
  generated: GeneratedPlatformMedia;
  evidence: MediaCitation[];
  coreResult: AgentControlRunResult;
  style?: MediaStyleProvenance;
}): MediaPlatformDraft {
  const generated = normalizeMediaPlatformGenerated(input.generated);
  const cited = validateMediaPlatformGrounding(generated, input.evidence);
  const citedSet = new Set(cited);
  const template = mediaPlatformTemplates[generated.type];
  const base = {
    id: input.id,
    status: "draft" as const,
    publicationStatus: "not_published" as const,
    trigger: "manual" as const,
    templateId: template.id,
    templateVersion: template.version,
    objective: input.objective,
    title: generated.content.title,
    limitations: generated.content.limitations,
    confidence: Math.min(
      generated.content.confidence,
      input.coreResult.synthesizer.confidence,
      input.coreResult.evaluator.score,
    ),
    citations: input.evidence.filter((record) => citedSet.has(record.number)),
    agentRunId: input.agentRunId,
    evaluatorScore: input.coreResult.evaluator.score,
    consultedDomains: (input.coreResult.domains ?? [])
      .filter((domain) => domain.status !== "not_consulted")
      .map((domain) => domain.id),
    generatedAt: input.generatedAt,
    style: input.style,
  };
  return { ...base, type: generated.type, content: generated.content } as MediaPlatformDraft;
}

export function defaultMediaObjective(state: AsymmetricState, type: MediaOutputType = "article") {
  const format = mediaOutputFormats.find((candidate) => candidate.id === type)?.label ?? "Media";
  const latest = state.signalPackets[0];
  if (latest) {
    return `Create the most decision-relevant Asymmetric ${format} draft grounded in the current engine, using “${latest.episode.title}” as the newest signal while testing it against all relevant sources, systems, timeline evidence, simulations, projections, and portfolio constraints.`;
  }
  return `Create the most decision-relevant Asymmetric ${format} draft grounded in the current engine state, integrating relevant sources, mechanisms, timeline evidence, simulations, projections, counterevidence, and portfolio constraints.`;
}
